import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Download, Trash2, UploadCloud, X } from "lucide-react";
import clsx from "clsx";
import { Demos, MAX_DEMO_BYTES, fileSize, type Demo } from "@/api/demos";
import { Accounts } from "@/api/endpoints";
import { apiErrorMessage } from "@/api/client";
import { confirmDialog, toast } from "@/lib/feedback";
import { fmtDay } from "@/lib/format";
import { useAuthStore } from "@/store/auth";
import DemoMedia from "./DemoMedia";
import { useDemoUrl } from "@/lib/useDemoUrl";

export function Modal({
  title,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  onClose: () => void;
  wide?: boolean;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4">
      <div className="overlay-backdrop" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={clsx(
          "panel relative max-h-[94dvh] w-full overflow-y-auto p-4 sm:p-6 animate-fade-up",
          wide ? "max-w-[980px]" : "max-w-[620px]",
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button className="btn btn-ghost !p-1.5" aria-label="Close" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Pick any number of accounts, with search. */
export function AccountsPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => Accounts.list() });
  const all = accountsQuery.data ?? [];
  const shown = all.filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <div className="rounded-xl border border-fg/[0.1]">
      <input
        className="w-full rounded-t-xl border-b border-fg/[0.08] bg-transparent px-3 py-2 text-md outline-none placeholder:text-mut-500"
        placeholder="Search accounts…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        aria-label="Search accounts"
      />
      <div className="max-h-44 overflow-y-auto p-1">
        {all.length === 0 && <p className="px-3 py-2 text-sm text-mut-500">No accounts yet.</p>}
        {shown.map((a) => {
          const on = value.includes(a.id);
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => toggle(a.id)}
              className={clsx(
                "flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-md",
                on ? "bg-accent-azure/10" : "hover:bg-fg/[0.05]",
              )}
            >
              <span
                className={clsx(
                  "grid h-4 w-4 place-items-center rounded border",
                  on ? "border-accent-azure bg-accent-azure text-white" : "border-fg/[0.25]",
                )}
              >
                {on && <Check className="h-3 w-3" />}
              </span>
              {a.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Upload one or more demos. Any file type, up to 500 MB each. */
export function UploadDialog({
  onClose,
  defaultAccountIds = [],
}: {
  onClose: () => void;
  defaultAccountIds?: string[];
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [title, setTitle] = useState("");
  const [product, setProduct] = useState("");
  const [description, setDescription] = useState("");
  const [accountIds, setAccountIds] = useState<string[]>(defaultAccountIds);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<{ index: number; fraction: number } | null>(null);

  const add = (list: FileList | null) => {
    if (!list) return;
    const picked = Array.from(list);
    const tooBig = picked.filter((f) => f.size > MAX_DEMO_BYTES);
    if (tooBig.length) toast.err(`Over 500 MB, skipped: ${tooBig.map((f) => f.name).join(", ")}`);
    setFiles((cur) => [...cur, ...picked.filter((f) => f.size <= MAX_DEMO_BYTES)]);
  };

  const upload = useMutation({
    mutationFn: async () => {
      for (const [index, file] of files.entries()) {
        setProgress({ index, fraction: 0 });
        await Demos.upload(
          {
            file,
            title: files.length === 1 ? title : undefined,
            product,
            description,
            accountIds,
          },
          (fraction) => setProgress({ index, fraction }),
        );
      }
    },
    onSuccess: () => {
      toast.ok(files.length === 1 ? "Demo uploaded" : `${files.length} demos uploaded`);
      void qc.invalidateQueries({ queryKey: ["demos"] });
      onClose();
    },
    onError: (e) => {
      setProgress(null);
      toast.err(`Upload failed: ${apiErrorMessage(e)}`);
      void qc.invalidateQueries({ queryKey: ["demos"] });
    },
  });

  const busy = upload.isPending;
  const total = files.reduce((n, f) => n + f.size, 0);
  return (
    <Modal title="Upload demos" onClose={busy ? () => undefined : onClose}>
      <div className="space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            add(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={clsx(
            "grid cursor-pointer place-items-center gap-2 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors",
            dragging
              ? "border-accent-azure bg-accent-azure/[0.06]"
              : "border-fg/[0.15] hover:border-fg/[0.25]",
          )}
        >
          <UploadCloud className="h-7 w-7 text-accent-azure" />
          <div className="text-md font-medium">Drop files here, or click to choose</div>
          <div className="text-sm text-mut-500">
            Video, audio, decks, PDFs, anything. Up to 500 MB each.
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              add(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {files.length > 0 && (
          <ul className="space-y-1.5">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="flex items-center gap-3 rounded-xl bg-ink-800 px-3 py-2 text-md"
              >
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                <span className="text-sm text-mut-500 tabular-nums">{fileSize(f.size)}</span>
                {progress && progress.index === i && (
                  <span className="w-24">
                    <span className="block h-1.5 overflow-hidden rounded-full bg-fg/[0.08]">
                      <span
                        className="block h-full rounded-full bg-accent-azure transition-[width]"
                        style={{ width: `${Math.round(progress.fraction * 100)}%` }}
                      />
                    </span>
                  </span>
                )}
                {progress && progress.index > i && <Check className="h-4 w-4 text-accent-green" />}
                {!busy && (
                  <button
                    className="text-mut-500 hover:text-mut-100"
                    aria-label={`Remove ${f.name}`}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {files.length === 1 && (
          <div>
            <label className="label" htmlFor="demo-title">
              Title
            </label>
            <input
              id="demo-title"
              className="input"
              placeholder={files[0].name.replace(/\.[^.]+$/, "")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="demo-product">
              Product
            </label>
            <input
              id="demo-product"
              className="input"
              placeholder="e.g. Tender Copilot"
              value={product}
              onChange={(e) => setProduct(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="demo-description">
              Description
            </label>
            <input
              id="demo-description"
              className="input"
              placeholder="What it shows, who it's for"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
        <div>
          <span className="label">Relevant accounts</span>
          <AccountsPicker value={accountIds} onChange={setAccountIds} />
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          {files.length > 1 && (
            <span className="mr-auto text-sm text-mut-500">{fileSize(total)} in total</span>
          )}
          <button className="btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!files.length || busy}
            onClick={() => upload.mutate()}
          >
            {busy
              ? `Uploading ${progress ? progress.index + 1 : 1} of ${files.length}…`
              : files.length > 1
                ? `Upload ${files.length} files`
                : "Upload"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** Watch / listen / view one demo, edit its details and linked accounts. */
export function DemoViewer({ demo: initial, onClose }: { demo: Demo; onClose: () => void }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [demo, setDemo] = useState(initial);
  const [editingAccounts, setEditingAccounts] = useState(false);
  const [accountIds, setAccountIds] = useState(demo.accounts.map((a) => a.id));
  const url = useDemoUrl(demo.id).data;
  const canDelete = demo.uploaded_by === me?.id || me?.role === "admin";
  const dirty = useMemo(
    () =>
      accountIds.slice().sort().join() !==
      demo.accounts
        .map((a) => a.id)
        .sort()
        .join(),
    [accountIds, demo.accounts],
  );

  const saveAccounts = useMutation({
    mutationFn: () => Demos.setAccounts(demo.id, accountIds),
    onSuccess: (d) => {
      setDemo(d);
      setEditingAccounts(false);
      void qc.invalidateQueries({ queryKey: ["demos"] });
      toast.ok("Accounts updated");
    },
    onError: (e) => toast.err(apiErrorMessage(e)),
  });

  const remove = async () => {
    const ok = await confirmDialog({
      title: "Delete this demo?",
      body: `“${demo.title}” will be removed for everyone, along with its links to accounts.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await Demos.remove(demo.id);
      toast.ok("Demo deleted");
      void qc.invalidateQueries({ queryKey: ["demos"] });
      onClose();
    } catch (e) {
      toast.err(apiErrorMessage(e));
    }
  };

  return (
    <Modal title={demo.title} onClose={onClose} wide>
      <div className="space-y-4">
        <DemoMedia demo={demo} />
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-mut-500">
          {demo.product && (
            <span className="chip-tint" style={{ ["--c" as string]: "var(--violet)" }}>
              {demo.product}
            </span>
          )}
          <span>{demo.filename}</span>
          <span>{fileSize(demo.size_bytes)}</span>
          <span>Added {fmtDay(demo.created_at)}</span>
          <span className="flex-1" />
          {url && (
            <a className="btn btn-ghost !py-1" href={`${url}&download=1`}>
              <Download className="h-3.5 w-3.5" /> Download
            </a>
          )}
          {canDelete && (
            <button className="btn btn-ghost !py-1 text-accent-pink" onClick={() => void remove()}>
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          )}
        </div>
        {demo.description && <p className="text-md text-mut-200">{demo.description}</p>}

        <div>
          <div className="mb-1.5 flex items-center gap-2">
            <span className="section-title">Relevant accounts</span>
            {!editingAccounts && (
              <button
                className="text-sm text-accent-azure hover:underline"
                onClick={() => setEditingAccounts(true)}
              >
                Edit
              </button>
            )}
          </div>
          {editingAccounts ? (
            <div className="space-y-2">
              <AccountsPicker value={accountIds} onChange={setAccountIds} />
              <div className="flex justify-end gap-2">
                <button
                  className="btn"
                  onClick={() => {
                    setAccountIds(demo.accounts.map((a) => a.id));
                    setEditingAccounts(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!dirty || saveAccounts.isPending}
                  onClick={() => saveAccounts.mutate()}
                >
                  Save
                </button>
              </div>
            </div>
          ) : demo.accounts.length ? (
            <div className="flex flex-wrap gap-1.5">
              {demo.accounts.map((a) => (
                <a
                  key={a.id}
                  href={`/accounts/${a.id}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-fg/[0.1] px-2.5 py-1 text-sm hover:border-fg/[0.2]"
                >
                  <Building2 className="h-3.5 w-3.5 text-mut-500" />
                  {a.name}
                </a>
              ))}
            </div>
          ) : (
            <p className="text-sm text-mut-500">Not linked to any account yet.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
